import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['textarea', 'dropdown'];
  static values = {
    emojis: String,
  };

  connect() {
    this.hideDropdown();
    this.currentSearch = '';
    this.isSearching = false;
    this.selectedIndex = 0;

    // Parse the emojis data
    this.parseEmojisData();

    // Bind the clickOutside method to this instance
    this.clickOutsideHandler = this.clickOutside.bind(this);

    // Add document click listener
    document.addEventListener('click', this.clickOutsideHandler);
  }

  disconnect() {
    // Clean up event listener when controller is disconnected
    document.removeEventListener('click', this.clickOutsideHandler);
  }

  // Handle clicks outside the dropdown
  clickOutside(event) {
    // If dropdown is not visible, do nothing
    if (!this.isSearching) return;

    // Check if the click was outside both the dropdown and the textarea
    const isOutsideDropdown = !this.dropdownTarget.contains(event.target);
    const isOutsideTextarea = !this.textareaTarget.contains(event.target);

    // If click was outside both elements, hide the dropdown
    if (isOutsideDropdown && isOutsideTextarea) {
      this.hideDropdown();
      this.isSearching = false;
    }
  }

  // Parse emojis data from various sources
  parseEmojisData() {
    try {
      // Try to parse the emojis value if it's a string
      if (
        typeof this.emojisValue === 'string' &&
        this.emojisValue.trim() !== ''
      ) {
        this.emojisData = JSON.parse(this.emojisValue);
      } else {
        // If no emoji data is provided via value, try to get it from the data target
        const dataElement = this.element.querySelector(
          '[data-emoji-autocomplete-target="data"]'
        );
        if (dataElement) {
          this.emojisData = JSON.parse(dataElement.textContent);
        } else {
          this.emojisData = { emojis: [] };
        }
      }
    } catch (e) {
      console.error('Failed to parse emoji data:', e);
      this.emojisData = { emojis: [] };
    }
  }

  hideDropdown() {
    this.dropdownTarget.classList.add('hidden');
  }

  showDropdown() {
    this.dropdownTarget.classList.remove('hidden');
  }

  onKeyUp(event) {
    const textarea = this.textareaTarget;
    const cursorPosition = textarea.selectionStart;
    const text = textarea.value.substring(0, cursorPosition);

    // Check if we're in an emoji search context (after a colon)
    const colonIndex = text.lastIndexOf(':');

    if (colonIndex !== -1 && colonIndex < cursorPosition - 1) {
      const searchTerm = text
        .substring(colonIndex + 1, cursorPosition)
        .toLowerCase();

      // Only search if we have at least 2 characters
      if (searchTerm.length >= 2) {
        this.currentSearch = searchTerm;
        this.isSearching = true;
        this.searchEmojis(searchTerm);
        this.showDropdown();
        this.selectedIndex = 0;
        return;
      }
    }

    // Hide dropdown if we're not in a search context
    if (this.isSearching) {
      this.hideDropdown();
      this.isSearching = false;
    }
  }

  searchEmojis(searchTerm) {
    // Make sure we have parsed emoji data
    if (!this.emojisData) {
      this.parseEmojisData();
    }

    // Get the emojis array
    const emojisArray = Array.isArray(this.emojisData)
      ? this.emojisData
      : this.emojisData.emojis || [];

    // Filter emojis that match the search term
    const matchingEmojis = emojisArray.filter((emoji) => {
      // Check if emoji name or keywords match the search term
      return (
        (emoji.name && emoji.name.toLowerCase().includes(searchTerm)) ||
        (emoji.keywords &&
          emoji.keywords.some((k) => k.toLowerCase().includes(searchTerm)))
      );
    });

    this.renderResults(matchingEmojis);
  }

  renderResults(results) {
    const dropdown = this.dropdownTarget;
    dropdown.innerHTML = '';

    if (results.length === 0) {
      return;
    }

    // Limit to first 15 emojis for performance
    const limitedResults = results.slice(0, 15);

    const emojiContainer = document.createElement('div');
    emojiContainer.className = 'flex flex-wrap gap-2 p-2';

    limitedResults.forEach((emoji) => {
      const emojiButton = document.createElement('button');
      emojiButton.type = 'button';
      emojiButton.className =
        'emoji-item text-xl hover:bg-base-200 p-1 rounded cursor-pointer';

      emojiButton.textContent = emoji.emoji;
      emojiButton.dataset.emoji = emoji.emoji;

      // Add tooltip with emoji name
      emojiButton.title = emoji.name;

      emojiButton.dataset.action = 'click->emoji-autocomplete#selectEmoji';
      emojiContainer.appendChild(emojiButton);
    });

    dropdown.appendChild(emojiContainer);
  }

  selectEmoji(event) {
    event.preventDefault();
    const emoji = event.currentTarget.dataset.emoji;
    const textarea = this.textareaTarget;
    const cursorPosition = textarea.selectionStart;
    const text = textarea.value;

    // Find the position of the colon that started the search
    const colonIndex = text.substring(0, cursorPosition).lastIndexOf(':');

    if (colonIndex !== -1) {
      // Replace the search text with the selected emoji
      const newText =
        text.substring(0, colonIndex) + emoji + text.substring(cursorPosition);
      textarea.value = newText;

      // Move cursor after the inserted emoji
      const newPosition = colonIndex + emoji.length;
      textarea.setSelectionRange(newPosition, newPosition);
      textarea.focus();
    }

    this.hideDropdown();
    this.isSearching = false;
  }

  onKeyDown(event) {
    if (!this.isSearching) return;

    // Handle keyboard navigation in dropdown
    if (
      event.key === 'ArrowDown' ||
      event.key === 'ArrowUp' ||
      event.key === 'Enter' ||
      event.key === 'Escape'
    ) {
      event.preventDefault();

      if (event.key === 'Escape') {
        this.hideDropdown();
        this.isSearching = false;
        return;
      }

      if (event.key === 'Enter') {
        const selectedEmoji = this.dropdownTarget.querySelector(
          '.emoji-item.selected'
        );
        if (selectedEmoji) {
          selectedEmoji.click();
        }
        return;
      }

      const emojiItems = this.dropdownTarget.querySelectorAll('.emoji-item');
      if (emojiItems.length === 0) return;

      // Remove current selection
      const currentSelected = this.dropdownTarget.querySelector(
        '.emoji-item.selected'
      );
      if (currentSelected) {
        currentSelected.classList.remove('selected', 'bg-base-200');
      }

      // Calculate new index
      if (event.key === 'ArrowDown') {
        this.selectedIndex = (this.selectedIndex + 1) % emojiItems.length;
      } else if (event.key === 'ArrowUp') {
        this.selectedIndex =
          (this.selectedIndex - 1 + emojiItems.length) % emojiItems.length;
      }

      // Apply selection to new item
      const newSelected = emojiItems[this.selectedIndex];
      newSelected.classList.add('selected', 'bg-base-200');
      newSelected.scrollIntoView({ block: 'nearest' });
    }
  }

  // When emojis value changes, parse the data
  emojisValueChanged() {
    this.parseEmojisData();
  }
}
